import React, { useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { BreathAPIs } from '../APIs/BreathAPIs';
import { ButtonCustom } from '../BootstrapComponents/ButtonCustom';
import { toast } from 'react-toastify';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

// Holds the estimated amount on the guest's card BEFORE the booking is created
// (at "Book" button press) - onSuccess(paymentIntentId) is only called once the
// hold is actually authorized, so the caller can pass that id along when it
// creates the booking. No booking exists yet at this point.
// The hold (PaymentIntent) is only created when the guest actually clicks
// "Authorize & Book" - not eagerly when the card form first appears - mirroring
// the same submit-then-create-intent pattern used in StripePayment/CheckOutForm.js.
const GuestPaymentForm = ({ amount, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setIsLoading(true);
    try {
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        toast.error(submitResult.error.message || 'Payment details invalid');
        setIsLoading(false);
        return;
      }

      const holdRes = await BreathAPIs.createGuestPaymentHold(amount);
      if (!holdRes?.data?.success || !holdRes?.data?.clientSecret) {
        toast.error(holdRes?.data?.message || 'Could not start payment authorization');
        setIsLoading(false);
        return;
      }
      const { clientSecret, paymentIntentId } = holdRes.data;

      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: process.env.REACT_APP_BASE_URL },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment failed');
        setIsLoading(false);
        return;
      }

      const confirmRes = await BreathAPIs.confirmGuestPaymentHold(paymentIntentId);
      if (confirmRes?.data?.success) {
        onSuccess(paymentIntentId);
      } else {
        toast.error('Could not authorize card. Please try again.');
      }
    } catch (err) {
      console.error('Guest booking payment error', err);
      toast.error('Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <p>
        We&#39;ll hold ${amount?.toFixed(2)} on your card (2x your estimate, as a buffer)
        to cover your session in a single transaction. You&#39;ll only be charged the
        final amount once your charging session ends.
      </p>
      <PaymentElement />
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <ButtonCustom
          variant="primary"
          text={isLoading ? 'Authorizing...' : 'Authorize & Book'}
          isLoading={isLoading}
          disabled={!stripe || isLoading}
          onClick={handlePay}
        />
        <ButtonCustom variant="secondary" text="Cancel" disabled={isLoading} onClick={onCancel} />
      </div>
    </div>
  );
};

const GuestBookingPayment = ({ amount, onSuccess, onCancel }) => {
  const [heldAmount] = useState((amount || 0) * 2);

  const options = {
    mode: 'payment',
    currency: 'usd',
    amount: Math.round((heldAmount || 0) * 100),
    capture_method: 'manual',
    setup_future_usage: 'off_session',
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <GuestPaymentForm amount={heldAmount} onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
};

export default GuestBookingPayment;
